import React, { useEffect, useState } from "react";
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  Button, TextField, Checkbox, FormControlLabel, Typography
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

import ModelField from "../PromptModal/formFields/ModelField";

const AssistantSchema = Yup.object().shape({
  name: Yup.string().required("Obrigatório"),
  description: Yup.string().required("Obrigatório"),
  system_prompt: Yup.string().required("Obrigatório"),
  model: Yup.string().required("Obrigatório"),
});

const AssistantModal = ({ open, onClose, assistantId, refreshAssistants }) => {
  const [initialValues, setInitialValues] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      try {
        const { data: keysData } = await api.get("/apikey");
        const keys = Array.isArray(keysData) ? keysData : keysData.apiKeys || [];
        setApiKeys(keys);

        if (assistantId) {
          const { data } = await api.get(`/assistants/${assistantId}`);
          setInitialValues({
            ...data,
            apiKeyId: data.apiKeyId || "",
            tools: data.tools?.map(t => t.type) || [],
            active: data.active ?? true,
          });
        } else {
          setInitialValues({
            idassistant: "",
            name: "",
            description: "Assitente comercial, primeiro atendimento",
            model: "gpt-3.5-turbo",
            temperature: 0.7,
            top_p: 1.0,
            system_prompt: "Você é um assistente útil e educado.",
            tools: [],
            active: true,
            apiKeyId: keys.length === 1 ? keys[0].id : ""
          });
        }
      } catch (err) {
        toastError(err);
      }
    };

    fetchData();
  }, [open, assistantId]);

  const handleSubmit = async (values, actions) => {
    try {
      const payload = {
        ...values,
        tools: values.tools.map(type => ({ type })),
      };

      if (assistantId) {
        await api.put(`/assistants/${assistantId}`, payload);
        toast.success(i18n.t("assistant.messages.updated"));
      } else {
        await api.post("/assistants", payload);
        toast.success(i18n.t("assistant.messages.created"));
      }

      refreshAssistants();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      actions.setSubmitting(false);
    }
  };

  if (!initialValues) return null;

  const availableTools = [
    { value: "code_interpreter", label: i18n.t("assistant.modal.codeInterpreter") },
    { value: "file_search", label: i18n.t("assistant.modal.fileSearch") },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {assistantId ? i18n.t("assistant.modal.editTitle") : i18n.t("assistant.modal.addTitle")}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={AssistantSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <label>{i18n.t("assistant.modal.apiKeyLabel")}</label>
                  <select
                    name="apiKeyId"
                    value={values.apiKeyId}
                    onChange={(e) => setFieldValue("apiKeyId", e.target.value)}
                    style={{ width: "100%", padding: 8 }}
                  >
                    {apiKeys.map((key) => (
                      <option key={key.id} value={key.id}>
                        {`${key.name} || ${key.key}`}
                      </option>
                    ))}
                  </select>
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="active"
                        checked={values.active}
                        onChange={(e) => setFieldValue("active", e.target.checked)}
                      />
                    }
                    label={i18n.t("assistant.modal.active")}
                  />
                </Grid>

                {assistantId && (
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="idassistant"
                      label={i18n.t("assistant.modal.idassistant")}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    fullWidth
                    name="name"
                    label={i18n.t("assistant.modal.name")}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    multiline
                    minRows={2}
                    name="description"
                    label={i18n.t("assistant.modal.description")}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    fullWidth
                    multiline
                    minRows={4}
                    name="system_prompt"
                    label={i18n.t("assistant.modal.system_prompt")}
                  />
                </Grid>

                <Grid item xs={12}>
                  <ModelField setFieldValue={setFieldValue} />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    type="number"
                    fullWidth
                    name="temperature"
                    label={i18n.t("assistant.modal.temperature")}
                    inputProps={{ step: 0.1, min: 0, max: 2 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    type="number"
                    fullWidth
                    name="top_p"
                    label="Top P"
                    inputProps={{ step: 0.1, min: 0, max: 1 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {i18n.t("assistant.modal.toolsTitle")}
                  </Typography>
                  {availableTools.map((tool) => (
                    <FormControlLabel
                      key={tool.value}
                      control={
                        <Checkbox
                          name="tools"
                          value={tool.value}
                          checked={values.tools.includes(tool.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFieldValue("tools", [...values.tools, tool.value]);
                            } else {
                              setFieldValue("tools", values.tools.filter((t) => t !== tool.value));
                            }
                          }}
                        />
                      }
                      label={tool.label}
                    />
                  ))}
                </Grid>
              </Grid>

              <Typography
                variant="caption"
                color="textSecondary"
                align="center"
                style={{ marginTop: 16 }}
              >
                Para um melhor refinamento de configurações pode ser necessário acessar{" "}
                <a href="https://platform.openai.com/" target="_blank" rel="noopener noreferrer">
                  https://platform.openai.com/
                </a>
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} color="default">
                {i18n.t("assistant.buttons.cancel")}
              </Button>
              <Button type="submit" color="primary" disabled={isSubmitting}>
                {i18n.t("assistant.buttons.save")}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AssistantModal;
